import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  Layers,
  Pencil,
  Plus,
  Power,
  Search,
  SlidersHorizontal,
} from "lucide-react";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import Modal from "src/components/ui/Modal";
import PlanFormModal from "./PlanFormModal";
import PlanPriceCard from "src/components/cards/PlanPriceCard";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as planActions from "src/redux/slices/planSlice";
import { formatDateTime } from "src/utils/date-time";
import { Plan } from "src/types/plan.types";
import { toast } from "react-toastify";
import { currencyFormatter } from "src/utils/common";

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
];

const PlanListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Plan | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [formModal, setFormModal] = useState<{ open: boolean; plan: Plan | null }>({
    open: false,
    plan: null,
  });
  const [previewPlan, setPreviewPlan] = useState<Plan | null>(null);

  const { plansList, editPlan } = useAppSelector((state) => state.plan);
  const dispatch = useAppDispatch();

  const hasActiveFilters = Boolean(search || statusFilter);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getPlanList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  // ✅ Success/Error side-effects for the status-toggle confirmation modal.
  // Guarded to only fire while that modal is actually open — PlanFormModal
  // (the full edit form) shares the same editPlan slice and handles its own
  // success/error toast, so reacting unconditionally here would double it.
  useEffect(() => {
    if (!showModal) return;
    if (editPlan.data) {
      toast.success("Plan modified successfully");
      dispatch(planActions.resetEditPlan());
      handleModalClose();
      getPlanList(1);
    } else if (editPlan.error) {
      toast.error(`Error: ${editPlan.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPlan, showModal]);

  const getPlanList = (page: number) =>
    dispatch(
      planActions.getPlanListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(statusFilter
          ? { status: statusFilter as "active" | "suspended" }
          : {}),
      })
    );

  const handlePageChange = (page: number) => {
    getPlanList(page);
  };

  const handlePlanStatus = (planId: string, status: "active" | "suspended") => {
    const payload = {
      _id: planId,
      status: status,
    };
    dispatch(planActions.editPlanRequest(payload));
  };

  const onConfirm = () => {
    if (selectedRow?._id) {
      handlePlanStatus(
        selectedRow?._id,
        selectedRow?.status === "active" ? "suspended" : "active"
      );
    }
  };

  const handleModalClose = () => {
    setSelectedRow(null);
    setShowModal(false);
  };

  const handleFormModalClose = () => {
    setFormModal({ open: false, plan: null });
    getPlanList(1);
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Plans | Invyntra",
        description:
          "Track and manage all product plans with detailed customer and pricing information.",
        keywords: "plans, products, invoices, inventory, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Layers size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Plans
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage subscription plans for the platform.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              rounded="full"
              onClick={() => setFormModal({ open: true, plan: null })}
            >
              <Plus size={16} className="mr-1" /> Add Plan
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search plans..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />
            </div>
          )}
        </Card>

        <Card>
          {/* Table */}
          <TableV2
            columns={[
              {
                key: "planName",
                header: "Plan Name",
                accessor: ({ key, row }) => (
                  <div className="w-20">{row[key]}</div>
                ),
              },
              { key: "planCode", header: "Plan Code" },
              {
                key: "price",
                header: "Price",
                align: "right",
                accessor: ({ key, row }) => (
                  <div>{currencyFormatter(row[key])}</div>
                ),
              },
              { key: "durationInDays", header: "Duration" },
              {
                key: "createdAt",
                header: "Published On",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] || ""}
                    color={row?.[key] === "active" ? "green" : "red"}
                  />
                ),
              },
              {
                key: "showOnWebsite",
                header: "Website",
                accessor: ({ row }) => (
                  <Badge
                    text={row?.showOnWebsite ? "PUBLIC" : "INTERNAL"}
                    color={row?.showOnWebsite ? "green" : "gray"}
                  />
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ key, row }) => (
                  <div className="pr-2">
                    <button
                      className="p-2 rounded-md hover:bg-gray-100 text-gray-600 dark:hover:bg-gray-700 dark:text-gray-300"
                      onClick={() => setPreviewPlan(row)}
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                      onClick={() => setFormModal({ open: true, plan: row })}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className={`p-2 rounded-md ${
                        row.status == "active"
                          ? "hover:bg-red-100 text-red-600"
                          : "hover:bg-green-100 text-green-600"
                      }`}
                      onClick={() => {
                        setSelectedRow(row);
                        setShowModal(true);
                      }}
                    >
                      <Power size={14} />
                    </button>
                  </div>
                ),
              },
            ]}
            data={plansList?.data?.data}
            pagination={plansList?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      {showModal && selectedRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            selectedRow?.status === "active" ? "suspend" : "activate"
          } this plan?`}
          onConfirm={onConfirm}
          onCancel={handleModalClose}
          open={showModal}
          isLoading={editPlan?.loading}
        />
      )}

      {formModal.open && (
        <PlanFormModal
          open={formModal.open}
          plan={formModal.plan}
          onClose={handleFormModalClose}
        />
      )}

      {previewPlan && (
        <Modal
          open={Boolean(previewPlan)}
          onClose={() => setPreviewPlan(null)}
          title="Plan Preview"
          size="sm"
          bodyClassName="p-4 overflow-y-auto flex-1 bg-gray-50 dark:bg-gray-950"
        >
          <PlanPriceCard plan={previewPlan} />
        </Modal>
      )}
    </Layout>
  );
};

export default PlanListPage;
